/**
 * Centralized styling utilities for asset states (locked/hidden)
 * Ensures consistent styling across assets, clips, and keyframes
 * 
 * IMPORTANT: This system separates VISUAL styling from BEHAVIOR.
 * Visual states (locked/hidden) should NOT affect interaction behavior like hover.
 * Behavior is controlled separately in hover/click handlers.
 */

import {
  stateCursor, TIMELINE_LAYERS, LOCKED_FILTER, HIDDEN_CLIP_OPACITY, LANE_HIDDEN_OUTLINE, LANE_HIDDEN_OUTLINE_OFFSET, LANE_LOCKED_STRIPES
} from '../../utils';

export type AssetState = 'normal' | 'locked' | 'hidden' | 'locked-hidden';

export interface AssetStateStyles {
  // Visual styling only - does NOT affect behavior
  filter?: string;
  border?: string;
  opacity?: number;
  background?: string;
  // Cursor styling (visual only - interaction behavior is separate)
  cursor?: string;
  // Z-index for overlays
  overlayZIndex?: number;
}

/**
 * Get styling for hidden/invisible assets (VISUAL ONLY)
 */
export const getHiddenAssetStyles = (): AssetStateStyles => ({
  opacity: HIDDEN_CLIP_OPACITY, // For clips
  cursor: stateCursor('noEntry'),
  overlayZIndex: TIMELINE_LAYERS.laneStateOverlay // Above clips (z-index 1-2)
});

/**
 * Get styling for locked assets (VISUAL ONLY)
 */
export const getLockedAssetStyles = (): AssetStateStyles => ({
  filter: LOCKED_FILTER,
  cursor: stateCursor('lock'),
  overlayZIndex: TIMELINE_LAYERS.laneStateOverlay // Above clips (z-index 1-2)
});

/**
 * Get combined styling for multiple states (extensible for future states)
 */
export const getCombinedAssetStyles = (states: AssetState[]): AssetStateStyles => {
  let combined: AssetStateStyles = {};
  
  states.forEach(state => {
    if (state === 'hidden') {
      const hidden = getHiddenAssetStyles();
      combined = { ...combined, ...hidden };
    } else if (state === 'locked') {
      const locked = getLockedAssetStyles();
      // Merge styles, with locked taking precedence for filter/cursor
      combined = {
        ...combined,
        ...locked,
        // Preserve hidden border/opacity if also hidden
        border: combined.border || locked.border,
        opacity: combined.opacity || locked.opacity
      };
    }
  });
  
  return combined;
};

/**
 * Get overlay style for hidden assets (dashed border)
 */
export const getHiddenOverlayStyle = () => {
  const styles = getHiddenAssetStyles();
  return {
    outline: LANE_HIDDEN_OUTLINE,
    outlineOffset: LANE_HIDDEN_OUTLINE_OFFSET,
    zIndex: styles.overlayZIndex
  };
};

/**
 * Get overlay style for locked assets (diagonal stripe pattern)
 */
export const getLockedOverlayStyle = () => {
  const styles = getLockedAssetStyles();
  return {
    background: LANE_LOCKED_STRIPES,
    zIndex: styles.overlayZIndex
  };
};

/**
 * Get clip styling based on asset state (VISUAL ONLY - behavior is separate)
 */
export const getClipStyles = (isLocked: boolean, isVisible: boolean): React.CSSProperties => {
  const states: AssetState[] = [];
  if (isLocked) states.push('locked');
  if (!isVisible) states.push('hidden');
  
  const styles = getCombinedAssetStyles(states);
  
  return {
    filter: styles.filter,
    border: styles.border,
    opacity: styles.opacity,
    cursor: styles.cursor // Apply cursor to clips too
  };
};

/**
 * Get asset container cursor style (VISUAL ONLY)
 */
export const getAssetCursor = (isLocked: boolean, isVisible: boolean, defaultCursor: string): string => {
  // Hidden takes precedence (no entry cursor)
  if (!isVisible) {
    const hiddenStyles = getHiddenAssetStyles();
    return hiddenStyles.cursor || defaultCursor;
  }
  // Locked cursor
  if (isLocked) {
    const lockedStyles = getLockedAssetStyles();
    return lockedStyles.cursor || defaultCursor;
  }
  return defaultCursor;
};

